import React, { useState } from "react";
import Grid from "../../Grid";
import Card from "../../Card";
import Spacing from "../../Spacing";
import { can_visit, capitalizeFirstLetter, notify, utcToUs } from "../../../helpers";
import SchedulerDetailsModal from "../../Modal/SchedulerDetailsModal";
import { EllipsisVertical, Eye, Repeat2, SendHorizonal, Trash2 } from "lucide-react";
import { deleteScheduler, resendScheduler, updateSchedulerStatus } from "../../../services/users";
import DeleteModal from "../../Modal/DeleteModal";
import ResendSchedulerConfirmModal from "../../Modal/ResendSchedulerConfirmModal";

export default function AllSchedulers({schedulerLoader, schedulerData, account_id, location_id, reloadData, setOpenNewScheduler, changeActiveTab, previousScrollPosition=0, setPreviousScrollPosition=()=>{} }) {
  const [schedulerDetails, setSchedulerDetails] = useState({});
  const [schedulerViewModal, setSchedulerViewModal] = useState(false);
  const [viewStatusOptions, setViewStatusOptions] = useState({});
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [activeDeleteModal, setActiveDeleteModal] = useState(false);
  const [resendSchedulerId, setResendSchedulerId] = useState(null);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendLoader, setResendLoader] = useState(false);

  const LimitText = ({ text, limit }) => {
    if (text.length <= limit) {
      return text;
    } else {
      const limitText = text.substring(0, limit) + "...";
      return limitText;
    }
  };

  const handleDropdownClick = (index) => {
    setViewStatusOptions((prev) => {
      const newViewStatusOptions = {};
      newViewStatusOptions[index] = !prev[index];
      return newViewStatusOptions;
    });
  };

  const changeStatus = async (id, status) => {
    const statusData = {
      account_id: account_id,
      location_id: location_id,
      scheduler_id: id,
      status: status,
    };
    try {
      const result = await updateSchedulerStatus(statusData);

      if(result){
        if(result?.detail) {
          notify("error", result?.detail);
        } else {
          if(result?.data?.status === "success") {
            notify("success", result?.data?.message);
            await reloadData(account_id, location_id);
          } else {
            notify("error", result?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally { }
  }

  const deleteSchedulerData = async (id) => {
    try {
      const result = await deleteScheduler(account_id, location_id, id);

      if(result){
        if(result?.detail) {
          notify("error", result?.detail);
        } else {
          if(result?.data?.status === "success") {
            notify("success", result?.data?.message);
            await reloadData(account_id, location_id);
          } else {
            notify("error", result?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally { }
  }

  const handelResendScheduler = async (id) => {
    setResendLoader(true);
    try {
      const resendParams = {
        account_id: account_id,
        location_id: location_id,
        scheduler_id: id
      }
      const result = await resendScheduler(resendParams);
      
      if(result){
        if(result?.detail) {
          notify("error", result?.detail, 0.5);
        } else {
          if(result?.data?.status === 'success'){
            setResendModalOpen(false);
            window.scrollTo(0, previousScrollPosition);
            setResendLoader(false);
            setResendSchedulerId(null);
            notify("success", result?.data?.message, 0.5);
            await reloadData(account_id, location_id);
          } else {
            notify("error", result?.data?.message, 0.5);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setResendLoader(false);
    }
  }

  return (
    <>
      {!schedulerLoader ? (
        schedulerData.length > 0 ? (
          <Card variant="docpro_card">
            <Grid variant="docpro_grid_col_3 docpro_grid_gap_20_16">
              {schedulerData?.map((item, index) => (
                <div key={index} className="docpro_border_heading_card w-full">
                  <div className="card_heading flex items-center" style={{ justifyContent: 'space-between' }}>
                    <h3 className="docpro_fs_16 docpro_mb_0 docpro_fw_700 docpro_secondary_color flex items-center docpro_grid_gap_4">
                      {item?.campaign_name && (
                        <LimitText text={item?.campaign_name} limit={30} />
                      )}
                    </h3>
                    <div className="dropdown-container relative">
                      <button className="docpro_icon_btn" onClick={() => handleDropdownClick(index)}>
                        <EllipsisVertical width={18} height={18} />
                        <span className="docpro_tip">Change Status</span>
                      </button>
                      {viewStatusOptions[index] && (
                        <div className={`docpro_button_toggle_options`}>
                          {item?.status === "active" ?
                            <p onClick={() => {changeStatus(item?.id, 'inactive'); setViewStatusOptions({});}} className="flex items-center docpro_grid_gap_4"><Repeat2 width={16} height={16} />Inactive</p>
                          :
                            <p onClick={() => {changeStatus(item?.id, 'active'); setViewStatusOptions({});}} className="flex items-center docpro_grid_gap_4"><Repeat2 width={16} height={16} />Active</p>
                          }
                          <p onClick={() => {setDeleteRecord(item?.id); setActiveDeleteModal(true); setViewStatusOptions({}); setPreviousScrollPosition(window.scrollY); window.scrollTo(0, 0);}} className="flex items-center docpro_grid_gap_4"><Trash2 width={16} height={16} /> Delete</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="card_body">
                    <table className="docpro_referral_table scheduler docpro_mb_0" style={{ textAlign: 'left' }}>
                      <tbody>
                        <tr>
                          <td className='docpro_ternary_color' style={{ width: '105px' }}>Target patients</td>
                          <td className="docpro_fw_500 text-right">{capitalizeFirstLetter(item?.target_patient_type)}</td>
                        </tr>
                        <tr>
                          <td className='docpro_ternary_color'>Send type</td>
                          <td className="docpro_fw_500 text-right">{capitalizeFirstLetter(item?.message_send_type)}</td>
                        </tr>
                        <tr>
                          <td className='docpro_ternary_color'>Last sent</td>
                          <td className="docpro_fw_500 text-right">
                            {item?.last_send_message_at ? 
                              `${utcToUs(item?.last_send_message_at)}`
                            : "" }
                          </td>
                        </tr>
                        <tr>
                          <td className='docpro_ternary_color'>Status</td>
                          <td className="text-right">
                            {item?.status === "active" ? 
                              <span className="docpro_badge docpro_badge_green">Active</span>
                            :
                              <span className="docpro_badge docpro_badge_red">Inactive</span>
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="card_footer">
                    <div className="flex items-center docpro_grid_gap_10" style={{ width: '100%' }}>
                      <button type="button"
                        className="docpro_bordered_btn docpro_fw_600 size_lg w-full"
                        style={{ padding: '9px 13px' }}
                        onClick={() => {
                          setSchedulerDetails(item);
                          setSchedulerViewModal(true);
                          setPreviousScrollPosition(window.scrollY);
                          window.scrollTo(0, 0);
                        }}
                      >
                        <Eye width={16} height={16} />
                        <span>View Details</span>
                      </button>
                      {item?.message_send_type === "now" && (
                        <button type="button"
                          className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg w-full"
                          style={{ padding: '9px 13px' }}
                          onClick={() => {
                            setResendModalOpen(true);
                            setPreviousScrollPosition(window.scrollY);
                            window.scrollTo(0, 0);
                            setResendSchedulerId(item?.id)
                          }}
                        >
                          <SendHorizonal width={16} height={16} />
                          <span>Send Now</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </Grid>
          </Card>
        ) : (
          <Card>
            <div className="sms-autopilot-home">
              <img src="/images/text_pilot.svg" alt="autopilot-logo" />
              <Spacing lg="5" sm="5" />
              <h4>Increase patient visits <br /> with automated messaging.</h4>
              <Spacing lg="5" sm="5" />
              <p className="docpro_fs_16">Connect with your patients before they even need to call you</p>
              {(can_visit("settings") && can_visit("settings_scheduler_setup")) && (
                <>
                  <Spacing lg="15" sm="15" />
                  <button
                    type="button"
                    className="docpro_bordered_btn docpro_fw_600 size_lg docpro_filed_btn"
                    onClick={() => {
                      setOpenNewScheduler(true);
                      setPreviousScrollPosition(window.scrollY);
                      window.scrollTo(0, 0);
                      changeActiveTab(1);
                    }}
                  >
                    Get Started
                  </button>
                  <Spacing lg="15" sm="15" />
                </>
              )}
            </div>
          </Card>
        )
      ) : (
        <div className="flex items-center loading" style={{ width: '100%', height: '290px', justifyContent: 'center' }}>
          <span style={{ position: 'inherit' }}>
          Loading...
          </span>
        </div>
      )}

      {schedulerViewModal && (
        <SchedulerDetailsModal 
          schedulerDetails={schedulerDetails}
          schedulerViewModal={schedulerViewModal}
          setSchedulerViewModal={setSchedulerViewModal}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {activeDeleteModal && (
        <DeleteModal
          handleDelete={() => deleteSchedulerData(deleteRecord)}
          name="this schedule"
          activeModal={activeDeleteModal}
          setActiveModal={setActiveDeleteModal}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
      )}

      {resendModalOpen && (
        <ResendSchedulerConfirmModal
          openModal={resendModalOpen}
          setOpenModal={setResendModalOpen}
          confirmAction={() => handelResendScheduler(resendSchedulerId)}
          resendLoader={resendLoader}
          setResendLoader={setResendLoader}
          account_id={account_id}
          location_id={location_id}
          resendSchedulerId={resendSchedulerId}
          setResendSchedulerId={setResendSchedulerId}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
      )}
    </>
  );
}
